import React from 'react';

/**
 * Keeps a copilot failure inside the copilot. Anything that throws while rendering below
 * this boundary shows `fallback` instead of unmounting the platform.
 *
 * `resetKeys`: when any value changes (e.g. the session id), the boundary clears itself.
 * `fallback`: ({ error, reset }) => element.
 */
export default class CopilotErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
        this.reset = this.reset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Visible in the console for debugging; never rethrown.
        console.error('[copilot] render failed', error, info?.componentStack);
        if (this.props.onError) {
            try {
                this.props.onError(error);
            } catch (_) {
                // the error reporter must not fail the boundary
            }
        }
    }

    componentDidUpdate(prevProps) {
        const prev = prevProps.resetKeys || [];
        const next = this.props.resetKeys || [];
        if (this.state.error && (prev.length !== next.length || prev.some((key, i) => key !== next[i]))) {
            this.reset();
        }
    }

    reset() {
        this.setState({ error: null });
    }

    render() {
        if (this.state.error) {
            const { fallback } = this.props;
            if (typeof fallback === 'function') {
                try {
                    return fallback({ error: this.state.error, reset: this.reset });
                } catch (_) {
                    return null;
                }
            }
            return fallback ?? null;
        }
        return this.props.children;
    }
}
